import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

export default function ResetarSenha() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");

  async function redefinirSenha(e) {
    e.preventDefault();
    setErro("");
    setMensagem("");

    if (!token) {
      setErro("Link inválido. Solicite uma nova recuperação de senha.");
      return;
    }

    if (!novaSenha || !confirmarSenha) {
      setErro("Preencha a nova senha e a confirmação.");
      return;
    }

    if (novaSenha.length < 6) {
      setErro("A nova senha precisa ter pelo menos 6 caracteres.");
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setErro("As senhas não conferem.");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch("https://app.nvplaydigital.com/resetar-senha", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          novaSenha,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Erro ao redefinir senha.");
      }

      setMensagem(data.message || "Senha redefinida com sucesso.");

      setTimeout(() => {
        navigate("/login");
      }, 1600);
    } catch (err) {
      setErro(err.message || "Erro ao conectar com servidor.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.glow1}></div>
      <div style={styles.glow2}></div>

      <div style={styles.card}>
        <div style={styles.logoArea}>
          <div style={styles.logo}>NV</div>
          <h1 style={styles.title}>Criar nova senha</h1>
          <p style={styles.subtitle}>
            Digite sua nova senha para voltar a acessar o painel.
          </p>
        </div>

        {erro && <div style={styles.errorBox}>{erro}</div>}
        {mensagem && <div style={styles.successBox}>{mensagem}</div>}

        {!token && (
          <div style={styles.errorBox}>
            Link inválido ou sem token. Volte e solicite uma nova recuperação.
          </div>
        )}

        <form onSubmit={redefinirSenha} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Nova senha</label>
            <div style={styles.passwordBox}>
              <input
                style={styles.passwordInput}
                type={mostrarSenha ? "text" : "password"}
                placeholder="Digite a nova senha"
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setMostrarSenha(!mostrarSenha)}
                style={styles.showButton}
              >
                {mostrarSenha ? "Ocultar" : "Mostrar"}
              </button>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirmar senha</label>
            <input
              style={styles.input}
              type={mostrarSenha ? "text" : "password"}
              placeholder="Confirme a nova senha"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
            />
          </div>

          <button type="submit" style={styles.button} disabled={loading || !token}>
            {loading ? "Salvando..." : "Redefinir senha"}
          </button>
        </form>

        <div style={styles.footer}>
          <Link to="/login" style={styles.link}>
            Voltar para o login
          </Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(135deg, #020617 0%, #0f172a 50%, #111827 100%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    position: "relative",
    overflow: "hidden",
    fontFamily: "Inter, Arial, sans-serif",
  },
  glow1: {
    position: "absolute",
    width: 300,
    height: 300,
    background: "#2563eb",
    borderRadius: "50%",
    filter: "blur(120px)",
    opacity: 0.25,
    top: -80,
    left: -80,
  },
  glow2: {
    position: "absolute",
    width: 300,
    height: 300,
    background: "#0ea5e9",
    borderRadius: "50%",
    filter: "blur(120px)",
    opacity: 0.2,
    bottom: -80,
    right: -80,
  },
  card: {
    width: "100%",
    maxWidth: 430,
    background: "rgba(15,23,42,0.92)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 28,
    padding: 32,
    backdropFilter: "blur(20px)",
    boxShadow: "0 25px 60px rgba(0,0,0,0.45)",
    position: "relative",
    zIndex: 2,
  },
  logoArea: {
    textAlign: "center",
    marginBottom: 28,
  },
  logo: {
    width: 76,
    height: 76,
    borderRadius: 24,
    background: "linear-gradient(135deg,#2563eb,#0ea5e9)",
    margin: "0 auto 18px auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "#fff",
    fontSize: 27,
    fontWeight: "bold",
    boxShadow: "0 15px 40px rgba(37,99,235,0.4)",
  },
  title: {
    color: "#fff",
    margin: 0,
    fontSize: 28,
    fontWeight: 800,
  },
  subtitle: {
    color: "#94a3b8",
    marginTop: 10,
    fontSize: 14,
    lineHeight: 1.5,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  label: {
    color: "#cbd5e1",
    fontSize: 14,
    fontWeight: 600,
  },
  input: {
    height: 54,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "#1e293b",
    padding: "0 18px",
    color: "#fff",
    fontSize: 15,
    outline: "none",
  },
  passwordBox: {
    display: "flex",
    alignItems: "center",
    background: "#1e293b",
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.08)",
    overflow: "hidden",
  },
  passwordInput: {
    flex: 1,
    height: 54,
    background: "transparent",
    border: "none",
    padding: "0 18px",
    color: "#fff",
    fontSize: 15,
    outline: "none",
    minWidth: 0,
  },
  showButton: {
    background: "transparent",
    border: "none",
    color: "#38bdf8",
    padding: "0 16px",
    cursor: "pointer",
    fontWeight: 600,
  },
  button: {
    marginTop: 6,
    height: 56,
    borderRadius: 18,
    border: "none",
    background: "linear-gradient(135deg,#2563eb,#0284c7)",
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 15px 35px rgba(37,99,235,0.35)",
  },
  errorBox: {
    background: "rgba(220,38,38,0.16)",
    border: "1px solid rgba(248,113,113,0.3)",
    color: "#fecaca",
    padding: 12,
    borderRadius: 14,
    marginBottom: 16,
    fontSize: 14,
  },
  successBox: {
    background: "rgba(22,163,74,0.16)",
    border: "1px solid rgba(74,222,128,0.3)",
    color: "#bbf7d0",
    padding: 12,
    borderRadius: 14,
    marginBottom: 16,
    fontSize: 14,
  },
  footer: {
    marginTop: 24,
    textAlign: "center",
  },
  link: {
    color: "#38bdf8",
    textDecoration: "none",
    fontWeight: 700,
    fontSize: 14,
  },
};
